import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import { EGG_KNOWN_ACTIONS, EggRoutingRule } from "../../types";
import { computeOverlaps } from "./ruleOverlap";

const EGG_KNOWN_SET = new Set<string>(EGG_KNOWN_ACTIONS);

const KNOWN_EVENT_COLORS: Record<
  string,
  "primary" | "secondary" | "warning" | "info"
> = {
  "lead.create.pre": "primary",
  "lead.create.post": "info",
  "lead.update.pre": "secondary",
  "campaign.lead.update.pre": "warning",
  "qualification.start": "info",
  "qualification.end": "primary",
  "qualification.pending": "secondary",
  "qualification.abort": "warning",
};

export default function EggRulesList() {
  const navigate = useNavigate();

  const [rules, setRules] = useState<EggRoutingRule[]>([]);
  const [filtered, setFiltered] = useState<EggRoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterName, setFilterName] = useState("");

  // Actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRule, setMenuRule] = useState<EggRoutingRule | null>(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<EggRoutingRule | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toggle loading per singola riga
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/v1/egg/rules");
      const data: EggRoutingRule[] = res.data.data.rules ?? [];
      console.log({ data, resData: res.data });
      const sorted = [...data].sort((a, b) => a.priority - b.priority);
      setRules(sorted);
      setFiltered(sorted);
    } catch (e) {
      console.error(e);
      setError("Impossibile caricare le regole EGG");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFilter = (value: string) => {
    setFilterName(value);
    const q = value.trim().toLowerCase();
    setFiltered(
      q ? rules.filter((r) => r.name.toLowerCase().includes(q)) : rules
    );
  };

  const handleToggleActive = async (rule: EggRoutingRule) => {
    setTogglingId(rule.id);
    try {
      await apiClient.patch(`/v1/egg/rules/${rule.id}`, { active: !rule.active });
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r))
      );
      setFiltered((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r))
      );
    } catch (e) {
      console.error(e);
      setError("Errore nell'aggiornamento dello stato");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/egg/rules/${deleteTarget.id}`);
      setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setFiltered((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      setError("Errore durante l'eliminazione della regola");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, rule: EggRoutingRule) => {
    setAnchorEl(e.currentTarget);
    setMenuRule(rule);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRule(null);
  };

  // Sovrapposizioni REALI: 2+ regole attive sullo stesso event_type che possono
  // co-scattare. Le coppie con condizioni mutuamente esclusive sono disinnescate.
  // `alwaysFireRuleIds` = regole attive senza condizioni (scattano sempre).
  const overlaps = useMemo(() => computeOverlaps(rules), [rules]);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Regole EGG Gateway
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/egg/rules/new")}
        >
          Nuova regola
        </Button>
      </Box>

      {/* Filtro */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Filtra per nome..."
          value={filterName}
          onChange={(e) => handleFilter(e.target.value)}
          sx={{ width: 300 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {overlaps.shared.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Sovrapposizione tra regole attive
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Su questi tipi di evento più regole possono scattare{" "}
            <strong>insieme</strong> (le loro condizioni non le distinguono):
            verranno eseguite tutte quelle che fanno match. Aggiungi condizioni
            mutuamente esclusive se non è il comportamento voluto.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {overlaps.shared.map((o) => (
              <li key={o.event}>
                <Typography variant="body2" component="span" fontFamily="monospace">
                  {o.event}
                </Typography>{" "}
                <Typography variant="body2" component="span" color="text.secondary">
                  → {o.rules.map((r) => r.name).join(", ")}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>
                  <Tooltip title="Priorità: 0 = massima. La prima regola che fa match vince.">
                    <span>Priorità</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Tipi evento</TableCell>
                <TableCell width={90}>Match</TableCell>
                <TableCell>Destinazione</TableCell>
                <TableCell width={80} align="center">Attiva</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nessuna regola trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Chip label={rule.priority} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {rule.name}
                      </Typography>
                      {rule.active && overlaps.alwaysFireRuleIds.has(rule.id) && (
                        <Tooltip title="Nessuna condizione di trigger: la regola scatta su ogni evento del suo tipo.">
                          <WarningAmberIcon color="warning" sx={{ fontSize: "1rem" }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {rule.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {rule.event_types.map((ev) => (
                        <Chip
                          key={ev}
                          label={ev}
                          size="small"
                          color={EGG_KNOWN_SET.has(ev) ? (KNOWN_EVENT_COLORS[ev] ?? "info") : "default"}
                          variant="outlined"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.match_mode === "all" ? "AND" : "OR"}
                      size="small"
                      color={rule.match_mode === "all" ? "info" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {rule.target_type === "chatbot_flow" ? (
                      <Chip label="Chatbot flow" size="small" color="success" variant="outlined" />
                    ) : rule.target_type === "egg_finance_update" ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                        <Chip label="EGG Finance (update)" size="small" color="primary" variant="outlined" />
                        <Chip
                          label={rule.target_egg_finance_update?.http_method ?? "PATCH"}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                        <Chip label="Webhook esterno" size="small" color="secondary" variant="outlined" />
                        {rule.target_external_webhook?.http_method && rule.target_external_webhook.http_method !== "POST" && (
                          <Chip
                            label={rule.target_external_webhook.http_method}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={rule.active}
                      size="small"
                      disabled={togglingId === rule.id}
                      onChange={() => handleToggleActive(rule)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => openMenu(e, rule)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuRule) navigate(`/egg/rules/${menuRule.id}`);
            closeMenu();
          }}
        >
          Modifica
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRule) {
              navigate("/egg/rules/new", {
                state: { duplicateFrom: menuRule },
              });
            }
            closeMenu();
          }}
        >
          Duplica
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTarget(menuRule);
            closeMenu();
          }}
          sx={{ color: "error.main" }}
        >
          Elimina
        </MenuItem>
      </Menu>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Elimina regola</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la regola{" "}
            <strong>{deleteTarget?.name}</strong>? Questa operazione non può
            essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Annulla
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
